import { ChangeDetectionStrategy, Component, Input, OnChanges } from "@angular/core";
import { CulturalActivity } from "@common/ADAPT.Common.Model/organisation/cultural-activity";
import { CulturalActivityType } from "@common/ADAPT.Common.Model/organisation/cultural-activity-type";
import { SpeedCatchup } from "@common/ADAPT.Common.Model/organisation/speed-catchup";
import { RatingValues } from "@common/ADAPT.Common.Model/organisation/speed-catchup-rating";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { ArrayUtilities } from "@common/lib/utilities/array-utilities";
import { UserService } from "@common/user/user.service";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { DateFormats } from "@common/ux/date-formats";
import { ActivitiesService } from "@org-common/lib/activities/activities.service";
import { CulturalActivityService } from "@org-common/lib/cultural-activity/cultural-activity.service";
import { PeerCatchupUiService } from "@org-common/lib/peer-catchup/peer-catchup-ui.service";
import moment from "moment";
import { filter, forkJoin, Observable } from "rxjs";
import { map, switchMap, tap } from "rxjs/operators";
import { PeerCatchupService } from "../peer-catchup.service";

@Component({
    selector: "adapt-catchup-grid",
    templateUrl: "./catchup-grid.component.html",
    styleUrls: ["./catchup-grid.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class CatchupGridComponent extends BaseComponent implements OnChanges {
    @Input() public filterForPersonId?: number;
    @Input() public filterForTeamId?: number;
    @Input() public activeOnly = true;

    public catchups?: SpeedCatchup[];
    private catchupActivities?: Map<SpeedCatchup, CulturalActivity>;
    private orgFrequency?: number;
    private catchupFrequencies?: Map<SpeedCatchup, number | undefined>;

    public constructor(
        private userService: UserService,
        private peerCatchupService: PeerCatchupService,
        private peerCatchupUiService: PeerCatchupUiService,
        private culturalActivityService: CulturalActivityService,
        private activitiesService: ActivitiesService,
    ) {
        super();

        // update grid when PCU activities update
        this.activitiesService.shouldUpdate$.pipe(
            filter((types) => types.includes(CulturalActivityType.PeerCatchUp)),
            this.takeUntilDestroyed(),
        ).subscribe(() => this.ngOnChanges());
    }

    public ngOnChanges() {
        let getCatchups$: Observable<SpeedCatchup[]>;
        if (this.filterForPersonId) {
            getCatchups$ = this.peerCatchupService.getLatestCatchupsForPerson(this.filterForPersonId, {}, this.activeOnly);
        } else if (this.filterForTeamId) {
            getCatchups$ = this.peerCatchupService.getLatestCatchupsForTeam(this.filterForTeamId);
        } else {
            getCatchups$ = this.peerCatchupService.getLatestCatchups({}, this.activeOnly);
        }

        // reset catchups to trigger spinner before querying
        this.catchups = undefined;
        getCatchups$.pipe(
            switchMap((catchups) => forkJoin([
                this.getCulturalActivityMap(catchups),
                this.peerCatchupService.getCatchupFrequency(),
                this.peerCatchupService.getCatchupFrequencyMonthsForCatchups(catchups),
            ]).pipe(
                tap(([eventMap, orgFrequency, teamFrequencies]) => {
                    this.catchupActivities = eventMap;
                    this.orgFrequency = orgFrequency;
                    this.catchupFrequencies = teamFrequencies;
                }),
                map(() => catchups),
            )),
            this.takeUntilDestroyed(),
        ).subscribe((catchups) => this.catchups = catchups);
    }

    @Autobind
    public getStatusClass(catchup: SpeedCatchup) {
        if (this.hasNoCadenceOrScheduledActivity(catchup)) {
            return "status-unscheduled";
        }

        const today = moment().startOf("day");
        const nextCatchup = this.getNextCatchupDateForCatchup(catchup);
        const monthFromToday = moment()
            .startOf("day")
            .add(1, "month");

        if (today.isAfter(nextCatchup)) {
            return "status-overdue";
        } else if (monthFromToday.isAfter(nextCatchup)) {
            return "status-due";
        } else {
            return "status-onschedule";
        }
    }

    @Autobind
    public calculateStatusCellValue(catchup: SpeedCatchup) {
        if (this.hasNoCadenceOrScheduledActivity(catchup)) {
            return "Unscheduled";
        }

        const today = moment().startOf("day");
        const nextCatchup = this.getNextCatchupDateForCatchup(catchup);
        const monthFromToday = moment()
            .startOf("day")
            .add(1, "month");

        if (today.isAfter(nextCatchup)) {
            // overdue
            let diff = today.diff(nextCatchup, "years");
            if (diff >= 1) {
                return `Overdue by ${diff} years`;
            }

            diff = today.diff(nextCatchup, "months");
            if (diff >= 1) {
                return `Overdue by ${diff} months`;
            }

            diff = today.diff(nextCatchup, "days");
            return `Overdue by ${diff} days`;
        } else if (monthFromToday.isAfter(nextCatchup)) {
            return "Due within a month";
        } else {
            return "On schedule";
        }
    }

    @Autobind
    public calculateParticipantsCellValue(catchup: SpeedCatchup) {
        if (this.filterForPersonId) {
            return this.otherPerson(catchup)?.fullName;
        } else {
            return `${catchup.person1?.fullName} & ${catchup.person2?.fullName}`;
        }
    }

    public calculateCreationDateDisplayValue(catchup: SpeedCatchup) {
        return moment(catchup.creationDate).format(DateFormats.moment.short);
    }

    public calculateCreationDateCellValue(catchup: SpeedCatchup) {
        return catchup.creationDate;
    }

    @Autobind
    public calculateNextCatchupCellValue(catchup: SpeedCatchup) {
        if (this.hasNoCadenceOrScheduledActivity(catchup)) {
            return null;
        }

        return this.getNextCatchupDateForCatchup(catchup).toDate();
    }

    @Autobind
    public calculateNextCatchupDisplayValue(catchup: SpeedCatchup) {
        if (this.hasNoCadenceOrScheduledActivity(catchup)) {
            return "N/A";
        }

        return this.getNextCatchupDateForCatchup(catchup).format(DateFormats.moment.short);
    }

    public calculateLatestCombinedRatingCellValue(catchup: SpeedCatchup) {
        return RatingValues[catchup.extensions.worstRatingValue];
    }

    public calculateLatestCombinedRatingDisplayValue(catchup: SpeedCatchup) {
        return catchup.extensions.worstRatingValue;
    }

    public calculateTeamCellValue(catchup: SpeedCatchup) {
        return catchup.team?.name ?? "All teams";
    }

    public calculateTeamGroupValue(catchup: SpeedCatchup) {
        return catchup.teamId;
    }

    public otherPerson(catchup: SpeedCatchup) {
        return catchup.person1Id !== this.filterForPersonId
            ? catchup.person1
            : catchup.person2;
    }

    public isInvolvedPerson(catchup: SpeedCatchup) {
        return catchup.extensions.isInvolvedPerson(this.userService.getCurrentPersonId());
    }

    public getNextActivityForCatchup(catchup: SpeedCatchup) {
        return this.catchupActivities?.get(catchup);
    }

    @Autobind
    public rescheduleCatchup(activity: CulturalActivity) {
        return this.peerCatchupUiService.rescheduleCatchupActivity(activity).pipe(
            tap(() => this.ngOnChanges()),
        );
    }

    @Autobind
    public scheduleCatchup(catchup: SpeedCatchup) {
        return this.peerCatchupUiService.scheduleCatchupFromPreviousCatchup(catchup).pipe(
            tap(() => this.ngOnChanges()),
        );
    }

    private hasCatchupFrequency(catchup: SpeedCatchup) {
        if (catchup.teamId) {
            const frequency = this.catchupFrequencies?.get(catchup);
            if (frequency !== undefined) {
                return frequency > 0;
            }
        }

        return this.orgFrequency !== undefined && this.orgFrequency > 0;
    }

    private hasNoCadenceOrScheduledActivity(catchup: SpeedCatchup) {
        return !this.hasCatchupFrequency(catchup) && !this.getNextActivityForCatchup(catchup);
    }

    private getNextCatchupDateForCatchup(catchup: SpeedCatchup) {
        const event = this.getNextActivityForCatchup(catchup);
        return event
            ? moment(event.date)
            : this.peerCatchupService.calculateNextCatchupDate(catchup.creationDate, catchup.teamId);
    }

    private getCulturalActivityMap(catchups: SpeedCatchup[]) {
        const personIds = ArrayUtilities.distinct(catchups.flatMap((catchup) => [catchup.person1Id, catchup.person2Id]));
        return this.culturalActivityService.getUpcomingCulturalActivitiesForPersonIds(personIds, [CulturalActivityType.PeerCatchUp]).pipe(
            map((activities) => {
                const catchupActivities = new Map<SpeedCatchup, CulturalActivity>();
                for (const catchup of catchups) {
                    const activity = activities.find((e) => ((e.person1Id === catchup.person1Id && e.person2Id === catchup.person2Id)
                        || (e.person2Id === catchup.person1Id && e.person1Id === catchup.person2Id))
                        && e.teamId === catchup.teamId);
                    if (activity) {
                        catchupActivities.set(catchup, activity);
                    }
                }
                return catchupActivities;
            }),
        );
    }
}
